import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import api from "../../api/axios"
import {
  CreditCard,
  DollarSign,
  TrendingUp,
  Calendar,
  Filter,
  FileText,
  Pencil,
  Eye,
} from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card"
import { StatsCard } from "../../components/ui/StatsCard"
import { Button } from "../../components/ui/Button"
import { SearchInput } from "../../components/ui/Input"
import { Select } from "../../components/ui/Select"
import { Badge } from "../../components/ui/Badge"
import { EmptyState } from "../../components/ui/EmptyState"
import { TableSkeleton } from "../../components/ui/Skeleton"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/Table"
import { fr, paymentMethodLabel, paymentTypeLabel } from "../../locales/fr"
import { downloadPaymentInvoice } from "../../services/paymentService"
import { PaymentInvoiceModal } from "../../components/payments/PaymentInvoiceModal"

export default function PaymentsList() {
  const navigate = useNavigate()
  const [payments, setPayments] = useState([])
  const [filteredPayments, setFilteredPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [methodFilter, setMethodFilter] = useState("all")
  const [invoiceModalId, setInvoiceModalId] = useState(null)

  const [stats, setStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    totalPayments: 0,
    avgPayment: 0,
  })

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    filterPayments()
    calculateStats()
  }, [payments, search, methodFilter])

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.get("/payments")
      setPayments(Array.isArray(res.data) ? res.data : [])
    } catch (e) {
      console.error("Failed to load payments", e)
    }
    setLoading(false)
  }

  const filterPayments = () => {
    let result = [...payments]

    if (search) {
      result = result.filter(
        (p) =>
          p.member?.toLowerCase().includes(search.toLowerCase()) ||
          p.id?.toString().includes(search)
      )
    }

    if (methodFilter !== "all") {
      result = result.filter((p) => p.method?.toLowerCase() === methodFilter.toLowerCase())
    }

    setFilteredPayments(result)
  }

  const calculateStats = () => {
    const total = payments.reduce((sum, p) => sum + (p.amount || 0), 0)
    const count = payments.length
    setStats({
      totalRevenue: total,
      monthlyRevenue: Math.round(total * 0.3),
      totalPayments: count,
      avgPayment: count > 0 ? Math.round(total / count) : 0,
    })
  }

  const getMethodBadge = (method) => {
    const key = method?.toLowerCase()
    switch (key) {
      case "cash":
        return <Badge variant="success">{fr.paymentMethod.cash}</Badge>
      case "card":
        return <Badge variant="default">{fr.paymentMethod.card}</Badge>
      case "transfer":
        return <Badge variant="secondary">{fr.paymentMethod.transfer}</Badge>
      default:
        return <Badge variant="outline">{paymentMethodLabel(method)}</Badge>
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{fr.payments.title}</h1>
          <p className="text-muted-foreground">{fr.payments.subtitle}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline">
            <FileText className="mr-2 h-4 w-4" />
            {fr.common.export}
          </Button>
          <Button>
            <CreditCard className="mr-2 h-4 w-4" />
            {fr.common.recordPayment}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title={fr.payments.stats.totalRevenue}
          value={`${stats.totalRevenue.toLocaleString()} MAD`}
          change={fr.dashboard.stats.changeFromLastMonth}
          changeType="positive"
          icon={DollarSign}
        />
        <StatsCard
          title={fr.payments.stats.monthlyRevenue}
          value={`${stats.monthlyRevenue.toLocaleString()} MAD`}
          change={fr.dashboard.stats.changeFromLastWeek}
          changeType="positive"
          icon={TrendingUp}
        />
        <StatsCard
          title={fr.payments.stats.totalPayments}
          value={stats.totalPayments}
          change={fr.dashboard.stats.newThisWeek}
          changeType="positive"
          icon={CreditCard}
        />
        <StatsCard
          title={fr.payments.stats.averagePayment}
          value={`${stats.avgPayment} MAD`}
          change={fr.common.stable}
          changeType="neutral"
          icon={Calendar}
        />
      </div>

      <Card className="p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <SearchInput
              placeholder={fr.payments.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <Select
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              className="w-44"
            >
              <option value="all">{fr.status.allMethods}</option>
              <option value="cash">{fr.paymentMethod.cash}</option>
              <option value="card">{fr.paymentMethod.card}</option>
              <option value="transfer">{fr.paymentMethod.transfer}</option>
            </Select>
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        {loading ? (
          <TableSkeleton rows={8} columns={8} />
        ) : filteredPayments.length === 0 ? (
          <EmptyState
            icon={CreditCard}
            title={fr.payments.emptyTitle}
            description={
              search || methodFilter !== "all"
                ? fr.payments.emptyFiltered
                : fr.payments.emptyInitial
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{fr.payments.table.id}</TableHead>
                <TableHead>{fr.payments.table.member}</TableHead>
                <TableHead>{fr.payments.table.amount}</TableHead>
                <TableHead>{fr.payments.table.method}</TableHead>
                <TableHead>{fr.tables.paymentType}</TableHead>
                <TableHead>{fr.payments.table.date}</TableHead>
                <TableHead className="w-[100px]">{fr.common.view}</TableHead>
                <TableHead className="text-right">{fr.tables.invoice}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>
                    <span className="font-mono text-sm text-muted-foreground">
                      #{payment.id}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-foreground">{payment.member}</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-semibold text-success">
                      {payment.amount} MAD
                    </span>
                  </TableCell>
                  <TableCell>{getMethodBadge(payment.method)}</TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{paymentTypeLabel(payment.paymentType)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{payment.createdAt}</span>
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/payments/${payment.id}`)}
                    >
                      <Eye className="mr-1 h-4 w-4" />
                      {fr.common.view}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title={fr.invoice.downloadPdf}
                        onClick={() => downloadPaymentInvoice(payment.id)}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title={fr.invoice.editInvoice}
                        onClick={() => setInvoiceModalId(payment.id)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {filteredPayments.length > 0 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-4">
            <p className="text-sm text-muted-foreground">
              {fr.pagination.payments(filteredPayments.length, payments.length)}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled>
                {fr.common.previous}
              </Button>
              <Button variant="outline" size="sm" disabled>
                {fr.common.next}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <PaymentInvoiceModal
        open={invoiceModalId != null}
        onClose={() => setInvoiceModalId(null)}
        paymentId={invoiceModalId}
        onSaved={load}
      />
    </div>
  )
}
